/**
 * The grilling turn's wire (M9, PRD §5.3).
 *
 * `POST /knock/{id}/turn` is the one call in this app that answers with a
 * stream rather than a body, so it does not go through `lib/api`'s
 * request-then-parse shape. It is read here instead: a `fetch` POST, a
 * `ReadableStream` reader, and a frame parser that turns `event:`/`data:`
 * pairs into a typed union.
 *
 * Three rules the parser keeps, because the transport is ours now (PLAN.md P6):
 * comment lines (`: hb`) are heartbeats and are dropped; an event name we have
 * no meaning for is skipped rather than guessed at; and a payload that will not
 * parse is skipped too. A stream that says something unexpected must never be
 * able to say something wrong.
 */
import {
  API_URL,
  normaliseHearingRoads,
  normaliseStanding,
  type HearingRoads,
  type Standing,
} from "./api";

/** As long a message as the server will read (400 past it). */
export const TURN_MAX = 2000;

/** The counter is noise until the limit is in sight. */
export const TURN_COUNTER_FROM = 1800;

/**
 * Where the turn is. `assessed` is the doorman having published a READ — never
 * "the conversation is over", because it is not (`docs/conversation-model.md`
 * §5.2): the sender may keep talking, and a turn that brings new evidence
 * publishes another one.
 */
export type TurnStatus = "listening" | "answering" | "assessed";

/**
 * What the doorman completed, in work products and never money. The shape is
 * per-kind (`claims_found`, `verified`/`refuted`, `sources_checked`), so it is
 * kept as the tolerant record it arrives as and read by the indicator that
 * turns it into a sentence.
 */
export type WorkOutcome = Record<string, number | boolean>;

export type TurnEvent =
  | { type: "state"; status: TurnStatus; turn: number; of: number }
  | { type: "token"; delta: string }
  /**
   * UNSAY IT (ruling R-0921-1). The provider's stream died a few words into the
   * doorman's reply and the server is asking again from scratch, so every token
   * of this turn so far is void: the half-line on screen goes, and the tokens
   * that follow are the reply. It carries no payload and arrives at most once.
   */
  | { type: "retract" }
  | { type: "notice"; content: string }
  /**
   * THE OWNER'S STEP AT THE READ (ruling R-0925-2 (2)): their line for this
   * kind of knocker, sent because the read just published met its condition —
   * a doorman line of its own, after the reply, already on the transcript.
   */
  | { type: "close"; content: string }
  /**
   * Work in flight (M10, FR-3.5). Not one of these carries a money figure:
   * they are the doorman working, rendered as work. Nothing on this stream
   * names money, and nothing around it does either — the accounting is the
   * plan's share of its period (owner decision 2026-08-21).
   */
  | { type: "work_started"; work_item_id: string; kind: string; label: string; units: number }
  | { type: "work_progress"; work_item_id: string; label: string; progress: number }
  | {
      type: "work_completed";
      work_item_id: string;
      label: string;
      units_executed: number;
      outcome: WorkOutcome;
    }
  /**
   * The budget, as a ratio and never as money (R6). `low` is the server saying
   * the plan cannot fund more vetting — the one moment the sender is shown the
   * roads out of it, which are the plan card's (ToU 2.2 §5.3) and no longer
   * "buy credits, or conclude". The ratio stays a ratio: a figure over this
   * stream is forbidden absolutely, and a PRICE is on the plan card beside the
   * control that agrees to it, not on the wire.
   */
  | { type: "budget"; consumed_ratio: number; show_bar: boolean; low: boolean }
  /**
   * The doorman's READ (M12, §2.4.2), emitted after the last token and work and
   * before `done`. It carries the receipt inline, so the screen never has to go
   * and read it separately.
   *
   * ON EVERY PUBLISHED ASSESSMENT, re-assessments included (§5.2). A turn that
   * changes the doorman's mind emits this again with the new standing, and the
   * screen simply draws the latest; a turn that changes nothing emits none.
   */
  | { type: "standing"; standing: Standing }
  /**
   * The turn is over — and the KNOCK is not (§5.2). Two facts about the row as
   * it stands: whether a read has been published, and whether the guard stopped
   * the doorman listening. There is no final status, because nothing here is
   * final: the gate is the recipient's and it does not move on this stream.
   */
  | { type: "done"; knock_id: string; assessed: boolean; guard_closed: boolean }
  /**
   * **THE HEARING IS PAUSED, NOT REFUSED** (ruling R-0921-2 (5)). The sender's
   * allowance could not cover the next piece of work, so the doorman stopped
   * where it stood: the knock is still open, what they wrote is still there,
   * nothing was spent and no vendor was called. It is not an `error` because a
   * client draws something different — an error ends a turn, and this has not
   * ended.
   *
   * `exhausted` carries the roads with no price on them (wave 53: no money
   * figure crosses a hearing's stream); the plan sheet reads the priced rung
   * from the plan route. The paid reset it once flagged is deleted (ruling
   * R-0923-3 (1)).
   */
  | { type: "paused"; exhausted: HearingRoads }
  /** Room arrived and the hearing picked up where it stopped (R-0921-2 (5)). */
  | { type: "resumed"; knock_id: string }
  /**
   * The server's own `error` event, and — synthesised, so callers have one
   * recovery path — every way the request can fail before the stream starts:
   * an HTTP refusal, an offline browser, a connection cut mid-answer.
   * `reason` carries the 409's `details.reason` when there is one.
   */
  | { type: "error"; code: string; reason: string | null };

export type SseFrame = { event: string; data: string };

/**
 * One frame's worth of lines into its name and payload. Returns `null` for a
 * block that carries no data — a heartbeat, or padding — which is the only
 * thing a comment-only block can mean.
 */
export function parseFrame(block: string): SseFrame | null {
  let event = "message";
  const data: string[] = [];

  for (const raw of block.split("\n")) {
    if (!raw || raw.startsWith(":")) continue; // heartbeats and blank padding
    const colon = raw.indexOf(":");
    const field = colon === -1 ? raw : raw.slice(0, colon);
    let value = colon === -1 ? "" : raw.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
    // `id` and `retry` are the spec's, not ours: this stream is not resumable.
  }

  return data.length > 0 ? { event, data: data.join("\n") } : null;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function count(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
}

/** A progress figure is a fraction: anything outside 0..1 is not one. */
function ratio(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** The work products, kept as themselves — only the numbers and flags the sentence reads. */
function outcomeOf(value: unknown): WorkOutcome {
  if (!value || typeof value !== "object") return {};
  const out: WorkOutcome = {};
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v === "number" && Number.isFinite(v)) out[key] = v;
    else if (typeof v === "boolean") out[key] = v;
  }
  return out;
}

/** A frame the screen can act on, or `null` — never a half-understood one. */
export function toTurnEvent(frame: SseFrame): TurnEvent | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(frame.data);
  } catch {
    return null;
  }
  const p = (parsed && typeof parsed === "object" ? parsed : {}) as Record<string, unknown>;

  switch (frame.event) {
    case "state": {
      const status = p["status"];
      if (status !== "listening" && status !== "answering" && status !== "assessed") return null;
      return { type: "state", status, turn: count(p["turn"]), of: count(p["of"]) };
    }
    case "token": {
      // An empty delta is legal and meaningless; a non-string is neither.
      const delta = p["delta"];
      return typeof delta === "string" ? { type: "token", delta } : null;
    }
    case "retract":
      // Nothing to read: the frame IS the instruction, and a payload on it
      // would be a payload nobody wrote.
      return { type: "retract" };
    case "notice": {
      const content = text(p["content"]);
      return content ? { type: "notice", content } : null;
    }
    case "close": {
      const content = text(p["content"]);
      return content ? { type: "close", content } : null;
    }
    case "work_started": {
      // A work item with no id and no label is a piece of work nothing can show.
      const id = text(p["work_item_id"]);
      const label = text(p["label"]);
      if (!id || !label) return null;
      return {
        type: "work_started",
        work_item_id: id,
        kind: text(p["kind"]) ?? "",
        label,
        units: count(p["units"]),
      };
    }
    case "work_progress": {
      const id = text(p["work_item_id"]);
      const label = text(p["label"]);
      if (!id || !label) return null;
      return { type: "work_progress", work_item_id: id, label, progress: ratio(p["progress"]) };
    }
    case "work_completed": {
      const id = text(p["work_item_id"]);
      const label = text(p["label"]);
      if (!id || !label) return null;
      return {
        type: "work_completed",
        work_item_id: id,
        label,
        units_executed: count(p["units_executed"]),
        outcome: outcomeOf(p["outcome"]),
      };
    }
    case "budget": {
      // Reassurance, not a meter (R6): the ratio, whether to show it at all,
      // and whether the hearing is running low. `low` is only ever true because
      // the server said so — a ratio is not a threshold this screen may guess.
      return {
        type: "budget",
        consumed_ratio: ratio(p["consumed_ratio"]),
        show_bar: p["show_bar"] === true,
        low: p["low"] === true,
      };
    }
    case "standing":
      // The whole object is coerced into shape; a band_label may be empty, and
      // the screen decides whether that is enough to show or worth re-reading.
      return { type: "standing", standing: normaliseStanding(p) };
    case "done":
      return {
        type: "done",
        knock_id: typeof p["knock_id"] === "string" ? p["knock_id"] : "",
        // Both read strictly. Anything but an explicit `true` leaves the screen
        // where it was — a read nobody published and a guard that never fired
        // are the states that claim nothing.
        assessed: p["assessed"] === true,
        guard_closed: p["guard_closed"] === true,
      };
    case "paused":
      // The details ride inside `details`, the shape every other 402 in this
      // product uses, so one parser reads both.
      return {
        type: "paused",
        exhausted: normaliseHearingRoads(p["details"] ?? {}),
      };
    case "resumed":
      return { type: "resumed", knock_id: text(p["knock_id"]) ?? "" };
    case "error":
      return { type: "error", code: text(p["code"]) ?? "UNKNOWN", reason: text(p["reason"]) };
    default:
      return null; // an event we have no meaning for is not one we act on
  }
}

/** Frames out of a byte stream, yielding each event as it completes. */
export async function* readEvents(body: ReadableStream<Uint8Array>): AsyncGenerator<TurnEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const drain = function* (final: boolean): Generator<TurnEvent> {
    for (;;) {
      const at = buffer.indexOf("\n\n");
      if (at === -1) break;
      const block = buffer.slice(0, at);
      buffer = buffer.slice(at + 2);
      const frame = parseFrame(block);
      const event = frame && toTurnEvent(frame);
      if (event) yield event;
    }
    // A well-behaved server ends every frame with a blank line, so this is
    // belt and braces: a last frame without its terminator is still read, and
    // a truncated one fails to parse and is dropped like any other nonsense.
    if (final && buffer.trim()) {
      const frame = parseFrame(buffer);
      buffer = "";
      const event = frame && toTurnEvent(frame);
      if (event) yield event;
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
      yield* drain(false);
    }
    buffer += decoder.decode();
    yield* drain(true);
  } finally {
    reader.releaseLock();
  }
}

/** What an HTTP refusal is called, when the body does not say. */
function statusCode(status: number, code: string | null): string {
  if (status === 401) return "UNAUTHENTICATED";
  if (code) return code;
  if (status === 404) return "KNOCK_NOT_FOUND";
  if (status === 429) return "RATE_LIMITED";
  if (status === 400) return "VALIDATION_ERROR";
  return "UNKNOWN";
}

/**
 * The sender's turn. Yields until the server says `done` — or, if the stream
 * ends without one, simply stops: a caller that has not seen `done` has not
 * had a turn, and says so in Nob's words rather than pretending.
 */
export async function* streamTurn(
  knockId: string,
  message: string,
  signal?: AbortSignal,
  /**
   * A VOICE NOTE SENT AS THE TURN (wave 76), in place of `message`: the id the
   * upload answered with. The server reads the note's transcript as the words.
   */
  voiceAttachmentId?: string | null,
): AsyncGenerator<TurnEvent> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}/api/v1/knock/${encodeURIComponent(knockId)}/turn`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json", accept: "text/event-stream" },
      body: JSON.stringify(
        voiceAttachmentId ? { voice_attachment_id: voiceAttachmentId } : { message },
      ),
      ...(signal ? { signal } : {}),
    });
  } catch {
    if (!signal?.aborted) yield { type: "error", code: "NETWORK", reason: null };
    return;
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { code?: unknown; details?: unknown };
    };
    const details = (
      body.error?.details && typeof body.error.details === "object" ? body.error.details : {}
    ) as Record<string, unknown>;
    yield {
      type: "error",
      code: statusCode(res.status, text(body.error?.code)),
      reason: text(details["reason"]),
    };
    return;
  }

  if (!res.body) {
    yield { type: "error", code: "NETWORK", reason: null };
    return;
  }

  try {
    yield* readEvents(res.body);
  } catch {
    // The connection went while Nob was mid-sentence. That is a retry, not a
    // refusal — the screen keeps what was typed and offers it back.
    if (!signal?.aborted) yield { type: "error", code: "NETWORK", reason: null };
  }
}
